// src/components/ChartPrepChecklist.jsx
// Epiphany MD — MA Chart Prep Checklist
//
// Used in two contexts:
//   1. SchedulePage — full MA workflow (readOnly=false)
//   2. PatientChartPage header — read-only status widget (readOnly=true)

import { useState, useEffect, useRef } from 'react';
import { getCurrentUserInfo } from '../lib/api';
import { db } from "../lib/db";
import { logAudit } from '../utils/auditLog';
import { todayISO } from '../utils/chartHelpers';
import {
  getSectionsForVisitType,
  getAllItemIds,
  getRequiredItemIds,
  VISIT_TYPES,
} from '../data/chartPrepData';

// ─── design tokens (dark theme) ──────────────────────────────────────────────
const C = {
  navy2: 'var(--navy-2)',
  navy3: 'var(--navy-3)',
  navy4: 'var(--navy-4)',
  teal: 'var(--teal)',
  tealLt: 'rgba(13,148,136,0.12)',
  green: 'var(--green, #22c55e)',
  greenLt: 'rgba(34,197,94,0.1)',
  amber: 'var(--amber, #f59e0b)',
  amberLt: 'rgba(245,158,11,0.1)',
  red: 'var(--red, #e05252)',
  redLt: 'rgba(224,82,82,0.1)',
  text1: 'var(--text-1)',
  text2: 'var(--text-2)',
  text3: 'var(--text-3)',
  border: 'var(--border)',
  border2: 'var(--border-2)',
};

function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// ─── SectionCard ──────────────────────────────────────────────────────────────
function SectionCard({ section, checkedItems, itemNotes, onToggle, onNote, readOnly, collapsed, onToggleCollapse }) {
  const done  = section.items.filter(i => checkedItems[i.id]).length;
  const total = section.items.length;
  const allDone = done === total;

  return (
    <div style={{
      border: `1px solid ${allDone ? 'rgba(34,197,94,0.25)' : C.border}`,
      borderRadius: 10,
      marginBottom: 8,
      background: C.navy3,
      overflow: 'hidden',
    }}>
      {/* Section header */}
      <button
        onClick={onToggleCollapse}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: allDone ? C.green : C.text1, textAlign: 'left' }}>
          {section.label}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{
            fontSize: 11, padding: '1px 8px', borderRadius: 20, fontWeight: 600,
            background: allDone ? C.greenLt : C.navy4,
            color: allDone ? C.green : C.text3,
            border: `1px solid ${allDone ? 'rgba(34,197,94,0.3)' : C.border}`,
          }}>
            {done}/{total}
          </span>
          <span style={{ color: C.text3, fontSize: 14, lineHeight: 1 }}>{collapsed ? '›' : '∨'}</span>
        </div>
      </button>

      {/* Items */}
      {!collapsed && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '4px 0' }}>
          {section.items.map(item => {
            const checked = !!checkedItems[item.id];
            const note    = itemNotes[item.id] || '';
            return (
              <div
                key={item.id}
                style={{
                  padding: '8px 14px',
                  borderBottom: `1px solid ${C.border}`,
                  opacity: readOnly && !checked ? 0.5 : 1,
                }}
              >
                {/* Checkbox row */}
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: readOnly ? 'default' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(item.id)}
                    disabled={readOnly}
                    style={{ accentColor: C.teal, width: 14, height: 14, marginTop: 2, flexShrink: 0, cursor: readOnly ? 'default' : 'pointer' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 13, color: checked ? C.text2 : C.text1,
                        textDecoration: checked ? 'line-through' : 'none',
                        lineHeight: 1.4,
                      }}>
                        {item.label}
                      </span>
                      {item.required && !checked && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                          background: C.redLt, color: C.red, border: `1px solid rgba(224,82,82,0.25)`,
                          flexShrink: 0,
                        }}>Required</span>
                      )}
                      {item.flag === 'warn' && (
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                          background: C.amberLt, color: C.amber, border: `1px solid rgba(245,158,11,0.25)`,
                          flexShrink: 0,
                        }}>{item.flagLabel || 'Verify'}</span>
                      )}
                    </div>
                    {item.sub && (
                      <div style={{ fontSize: 11, color: C.text3, marginTop: 2, lineHeight: 1.4 }}>
                        {item.sub}
                      </div>
                    )}
                    {/* MA note — shown when checked */}
                    {checked && !readOnly && (
                      <input
                        type="text"
                        placeholder="MA note (optional)…"
                        value={note}
                        onChange={e => onNote(item.id, e.target.value)}
                        onClick={e => e.stopPropagation()}
                        style={{
                          marginTop: 6, width: '100%', fontSize: 12,
                          padding: '4px 8px', borderRadius: 6, boxSizing: 'border-box',
                          background: C.navy4, border: `1px solid ${C.border2}`,
                          color: C.text1, fontFamily: 'inherit',
                        }}
                      />
                    )}
                    {checked && readOnly && note && (
                      <div style={{ fontSize: 11, color: C.text2, marginTop: 3, fontStyle: 'italic' }}>{note}</div>
                    )}
                  </div>
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ChartPrepChecklist({
  patientId,
  patientName,
  appointmentId,
  appointmentDate,
  providerName,
  visitType: initialVisitType = 'followup',
  practiceId,
  readOnly = false,
  onSignOff,
}) {
  const isValidUuid = patientId && /^[0-9a-f-]{36}$/i.test(patientId);
  const today = todayISO();
  const apptDate = appointmentDate || today;

  const [visitType,         setVisitType]         = useState(initialVisitType);
  const [checkedItems,      setCheckedItems]      = useState({});
  const [itemNotes,         setItemNotes]         = useState({});
  const [collapsed,         setCollapsed]         = useState({});
  const [loading,           setLoading]           = useState(true);
  const [saving,            setSaving]            = useState(false);
  const [savedRowId,        setSavedRowId]        = useState(null);
  const [signedOff,         setSignedOff]         = useState(null); // { ma_name, signed_off_at }
  const [maName,            setMaName]            = useState('');
  const [error,             setError]             = useState(null);

  const saveTimer = useRef(null);

  // ── derived ──────────────────────────────────────────────────────────────────
  const sections     = getSectionsForVisitType(visitType) || [];
  const allItemIds   = getAllItemIds(visitType)            || [];
  const requiredIds  = getRequiredItemIds(visitType)      || [];
  const totalItems   = allItemIds.length;
  const completedCount  = allItemIds.filter(id => checkedItems[id]).length;
  const requiredMissing = requiredIds.filter(id => !checkedItems[id]).length;
  const pct = totalItems ? Math.round((completedCount / totalItems) * 100) : 0;
  const canSignOff = maName.trim().length > 0 && requiredMissing === 0 && !signedOff;

  // ── load existing row ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isValidUuid) { setLoading(false); return; }
    loadChecklist();
  // eslint-disable-next-line
  }, [patientId, appointmentId]);

  // ── cleanup debounce on unmount ───────────────────────────────────────────────
  useEffect(() => {
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, []);

  async function loadChecklist() {
    setLoading(true);
    try {
      let q = db.from('chart_prep_checklists').select('*');
      const apptIdValid = appointmentId && /^[0-9a-f-]{36}$/.test(appointmentId);
      if (apptIdValid) {
        q = q.eq('appointment_id', appointmentId);
      } else {
        q = q.eq('patient_id', patientId).eq('appointment_date', apptDate);
      }
      const { data } = await q.maybeSingle();
      if (data) {
        setSavedRowId(data.id);
        setCheckedItems(data.completed_items || {});
        setItemNotes(data.item_notes || {});
        if (data.visit_type) setVisitType(data.visit_type);
        if (data.signed_off_at) {
          setSignedOff({ ma_name: data.ma_name, signed_off_at: data.signed_off_at });
        }
      }
      await logAudit('chart_prep_checklists', 'SELECT', patientId);
    } catch (err) {
      console.error('ChartPrepChecklist load:', err);
    } finally {
      setLoading(false);
    }
  }

  // ── autosave ──────────────────────────────────────────────────────────────────
  function scheduleAutosave(newChecked, newNotes, vt) {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave(newChecked, newNotes, vt), 600);
  }

  async function doSave(checked, notes, vt) {
    if (!isValidUuid || readOnly || signedOff) return;
    const ids     = getAllItemIds(vt);
    const reqIds  = getRequiredItemIds(vt);
    const done    = ids.filter(id => checked[id]).length;
    const missing = reqIds.filter(id => !checked[id]).length;
    const status  = done === 0 ? 'pending' : 'in_progress';
    const apptIdValid = appointmentId && /^[0-9a-f-]{36}$/.test(appointmentId);

    const row = {
      practice_id:      practiceId,
      patient_id:       patientId,
      appointment_id:   apptIdValid ? appointmentId : null,
      appointment_date: apptDate,
      visit_type:       vt,
      completed_items:  checked,
      item_notes:       notes,
      status,
      completed_count:  done,
      total_items:      ids.length,
      required_missing: missing,
    };

    try {
      setSaving(true);
      if (savedRowId) {
        await db.from('chart_prep_checklists').update(row).eq('id', savedRowId);
      } else {
        const { data, error: insErr } = await db
          .from('chart_prep_checklists').insert(row).select('id').single();
        if (insErr) throw insErr;
        if (data?.id) setSavedRowId(data.id);
      }
    } catch (err) {
      console.error('ChartPrepChecklist autosave:', err);
      setError('Autosave failed — changes may not have saved.');
    } finally {
      setSaving(false);
    }
  }

  // ── item interactions ─────────────────────────────────────────────────────────
  function toggleItem(itemId) {
    if (readOnly || signedOff) return;
    const next = { ...checkedItems };
    if (next[itemId]) { delete next[itemId]; } else { next[itemId] = true; }
    setCheckedItems(next);
    scheduleAutosave(next, itemNotes, visitType);
  }

  function updateNote(itemId, text) {
    if (readOnly || signedOff) return;
    const next = { ...itemNotes };
    if (text) { next[itemId] = text; } else { delete next[itemId]; }
    setItemNotes(next);
    scheduleAutosave(checkedItems, next, visitType);
  }

  function handleVisitTypeChange(vt) {
    setVisitType(vt);
    scheduleAutosave(checkedItems, itemNotes, vt);
  }

  function toggleSection(sectionId) {
    setCollapsed(c => ({ ...c, [sectionId]: !c[sectionId] }));
  }

  // ── sign-off ──────────────────────────────────────────────────────────────────
  async function handleSignOff() {
    if (!canSignOff || !isValidUuid) return;
    setSaving(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    try {
      const { uid } = await getCurrentUserInfo();
      const user = { id: uid }; // attribution only — ma_user_id below
      const now = new Date().toISOString();
      const ids = getAllItemIds(visitType);
      const apptIdValid = appointmentId && /^[0-9a-f-]{36}$/.test(appointmentId);

      const row = {
        practice_id:      practiceId,
        patient_id:       patientId,
        appointment_id:   apptIdValid ? appointmentId : null,
        appointment_date: apptDate,
        visit_type:       visitType,
        completed_items:  checkedItems,
        item_notes:       itemNotes,
        status:           'complete',
        completed_count:  ids.filter(id => checkedItems[id]).length,
        total_items:      ids.length,
        required_missing: 0,
        ma_name:          maName.trim(),
        ma_user_id:       user?.id || null,
        signed_off_at:    now,
      };

      let savedRow;
      if (savedRowId) {
        const { data } = await db
          .from('chart_prep_checklists').update(row).eq('id', savedRowId).select().single();
        savedRow = data;
      } else {
        const { data, error: insErr } = await db
          .from('chart_prep_checklists').insert(row).select().single();
        if (insErr) throw insErr;
        savedRow = data;
        if (data?.id) setSavedRowId(data.id);
      }

      setSignedOff({ ma_name: maName.trim(), signed_off_at: now });
      if (onSignOff) onSignOff(savedRow || row);
    } catch (err) {
      console.error('ChartPrepChecklist sign-off:', err);
      setError('Sign-off failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // ─── guard ────────────────────────────────────────────────────────────────────
  if (!isValidUuid) {
    return (
      <div style={{ padding: '1rem', fontSize: 13, color: C.text3 }}>
        No valid patient selected.
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '1.5rem', textAlign: 'center', fontSize: 13, color: C.text3 }}>
        Loading checklist…
      </div>
    );
  }

  // ─── render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: readOnly ? '8px 0' : '0' }}>

      {/* Signed-off banner */}
      {signedOff && (
        <div style={{
          background: C.greenLt, border: `1px solid rgba(34,197,94,0.3)`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ color: C.green, fontSize: 16 }}>✓</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.green }}>Chart signed off — ready for provider</div>
            <div style={{ fontSize: 11, color: C.text2, marginTop: 2 }}>
              {signedOff.ma_name} · {fmtTime(signedOff.signed_off_at)}
            </div>
          </div>
        </div>
      )}

      {/* Visit type tabs — hidden in readOnly */}
      {!readOnly && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
          {VISIT_TYPES.map(vt => (
            <button
              key={vt.id}
              onClick={() => handleVisitTypeChange(vt.id)}
              style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
                border: `1px solid ${visitType === vt.id ? C.teal : C.border}`,
                background: visitType === vt.id ? C.tealLt : 'transparent',
                color: visitType === vt.id ? C.teal : C.text2,
                fontWeight: visitType === vt.id ? 600 : 400,
                fontFamily: 'inherit',
              }}
            >
              {vt.label}
            </button>
          ))}
        </div>
      )}

      {/* Patient info row */}
      {(patientName || providerName) && !readOnly && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: 12, color: C.text2 }}>
          {patientName && <span style={{ fontWeight: 500, color: C.text1 }}>{patientName}</span>}
          {providerName && <span>{providerName}</span>}
          {appointmentDate && <span>{appointmentDate}</span>}
        </div>
      )}

      {/* Progress bar */}
      {!readOnly && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: C.text3 }}>
              {completedCount} / {totalItems} items complete
            </span>
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {saving && <span style={{ fontSize: 10, color: C.text3 }}>Saving…</span>}
              {requiredMissing > 0 && !signedOff && (
                <span style={{
                  fontSize: 10, padding: '1px 7px', borderRadius: 4,
                  background: C.redLt, color: C.red,
                  border: `1px solid rgba(224,82,82,0.25)`,
                }}>
                  {requiredMissing} required missing
                </span>
              )}
              <span style={{ fontSize: 11, color: pct === 100 ? C.green : C.text3, fontWeight: 600 }}>{pct}%</span>
            </span>
          </div>
          <div style={{ height: 4, background: C.navy4, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 4, transition: 'width 0.3s ease',
              width: `${pct}%`,
              background: pct === 100 ? C.green : C.teal,
            }} />
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div style={{
          background: C.redLt, border: `1px solid rgba(224,82,82,0.3)`, borderRadius: 8,
          padding: '8px 12px', fontSize: 12, color: C.red, marginBottom: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {error}
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.red, fontSize: 14 }}>×</button>
        </div>
      )}

      {/* Sections */}
      {sections.map(section => (
        <SectionCard
          key={section.id}
          section={section}
          checkedItems={checkedItems}
          itemNotes={itemNotes}
          onToggle={toggleItem}
          onNote={updateNote}
          readOnly={readOnly || !!signedOff}
          collapsed={!!collapsed[section.id]}
          onToggleCollapse={() => toggleSection(section.id)}
        />
      ))}

      {/* Sign-off section */}
      {!readOnly && !signedOff && (
        <div style={{
          marginTop: 16, padding: '14px 16px', background: C.navy3,
          border: `1px solid ${C.border}`, borderRadius: 10,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.text2, marginBottom: 10 }}>
            MA Sign-Off
          </div>
          {requiredMissing > 0 && (
            <div style={{ fontSize: 11, color: C.amber, marginBottom: 8 }}>
              ⚠ {requiredMissing} required item{requiredMissing > 1 ? 's' : ''} must be checked before sign-off.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Your name (MA / front desk)…"
              value={maName}
              onChange={e => setMaName(e.target.value)}
              style={{
                flex: 1, fontSize: 13, padding: '7px 10px', borderRadius: 8, boxSizing: 'border-box',
                background: C.navy4, border: `1px solid ${C.border2}`,
                color: C.text1, fontFamily: 'inherit',
              }}
            />
            <button
              onClick={handleSignOff}
              disabled={!canSignOff || saving}
              style={{
                padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: canSignOff && !saving ? 'pointer' : 'default',
                background: canSignOff && !saving ? C.teal : C.navy4,
                color: canSignOff && !saving ? '#fff' : C.text3,
                border: 'none', fontFamily: 'inherit', flexShrink: 0,
                transition: 'background 0.15s',
              }}
            >
              {saving ? 'Saving…' : 'Sign off — chart ready'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
